import clsx from 'clsx';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import type { AgentApiCandidateProfilesItem } from '@/types/serializers';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import type { Color } from '../types/CandidateBoard';
import { BOARD_MENU_ITEMS, type BoardCardFieldKey } from '../constants';
import useBoardCardVisibleFields, {
  type BoardCardVisibleFields,
} from '../hooks/useBoardCardVisibleFields';
import { TextField } from '@/components/TextField';
import { MaterialIcon } from '@/components/MaterialIcon';
import CandidatesSelectColorMenu from './CandidatesSelectColorMenu';
import CandidatesSelectSortMenu from './CandidatesSelectSortMenu';
import useDeleteCandidateBoard from '../api/useDeleteCandidateBoard';
import useSubmenuSide from '@/hooks/useSubmenuSide';
import { showErrorToast } from '../../job_change_processes/lib/toast';

type Props = {
  title: string;
  divideStatus: string;
  colorType: Color;
  profiles: AgentApiCandidateProfilesItem[];
  onProfileClick: (profileId: number) => void;
  onTitleChange?: (newTitle: string) => void;
  onColorChange?: (color: Color) => void;
  onDelete?: (id: number) => void;
  id?: number;
  isDraggable?: boolean;
  onLoadMore?: (boardId: number | string) => Promise<void>;
  hasNextPage?: boolean;
  isLoadingMore?: boolean;
  totalCount?: number;
  boards?: { records: { id: number; divide_status: string }[] };
  boardsRefetch?: (options?: { resetPages?: boolean }) => Promise<unknown>;
};

const throttle = <T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let lastCall = 0;
  return (...args: Parameters<T>) => {
    const now = Date.now();
    if (now - lastCall >= wait) {
      lastCall = now;
      return func(...args);
    }
  };
};

type CardDetailField = {
  key: BoardCardFieldKey;
  icon: string;
  value: string;
};

function CardDetailRow({ field }: { field: CardDetailField }) {
  return (
    <div className="flex items-center gap-xs">
      <MaterialIcon
        name={field.icon}
        filled
        className="shrink-0 text-base leading-none text-main"
      />
      <p className="min-w-0 flex-1 truncate text-body-xs text-main">{field.value}</p>
    </div>
  );
}

function DraggableProfileCard({
  profile,
  onProfileClick,
  index,
  visibleFields,
}: {
  profile: AgentApiCandidateProfilesItem;
  onProfileClick: (profileId: number) => void;
  index: number;
  visibleFields: BoardCardVisibleFields;
}) {
  const handleClick = () => {
    onProfileClick(profile.id);
  };

  const detailFields = [
    { key: 'company', icon: 'apartment', value: profile.company_name },
    { key: 'position', icon: 'work', value: profile.job_position },
    { key: 'currentSalary', icon: 'payments', value: profile.current_salary },
    { key: 'desiredSalary', icon: 'savings', value: profile.desired_salary },
  ].filter((field): field is CardDetailField =>
    Boolean(visibleFields[field.key as BoardCardFieldKey] && field.value)
  );

  const assignees = visibleFields.assignee ? (profile.organization_users ?? []) : [];

  return (
    <Draggable draggableId={String(profile.id)} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={handleClick}
          className={clsx(
            'hover:bg-background/gray flex cursor-pointer flex-col gap-xs rounded-sm bg-white px-sm py-md drop-shadow-[0px_0px_3px_var(--color-shadow)]',
            snapshot.isDragging && 'bg-gray_darken!'
          )}
          style={{
            ...provided.draggableProps.style,
          }}
        >
          <div className="flex flex-col gap-xs px-xxs">
            <div className="flex flex-wrap items-center gap-xs">
              <p className="text-heading-xxs text-main">
                {profile.name}
                {visibleFields.age && profile.age ? ` (${profile.age})` : ''}
              </p>
              {assignees[0] && (
                <span className="flex h-5 items-center justify-center gap-xxs rounded-full border border-solid border-border px-sm text-tag-sm whitespace-pre">
                  {assignees[0].name.length > 5
                    ? `${assignees[0].name.slice(0, 5)}...`
                    : assignees[0].name}
                  {assignees.length > 1 && (
                    <span className="text-primary">+{assignees.length - 1}</span>
                  )}
                </span>
              )}
            </div>
            {detailFields.length > 0 && <div className="min-h-[0.5px] w-full bg-border"></div>}
          </div>
          {detailFields.length > 0 && (
            <div className="flex flex-col gap-xxs px-xxs">
              {detailFields.map((field) => (
                <CardDetailRow key={field.key} field={field} />
              ))}
            </div>
          )}
        </div>
      )}
    </Draggable>
  );
}

export default function CandidatesBoardColumn({
  title,
  colorType,
  divideStatus,
  profiles,
  onProfileClick,
  onTitleChange,
  onColorChange,
  onDelete,
  id,
  onLoadMore,
  hasNextPage = false,
  isLoadingMore = false,
  totalCount,
  boards,
  boardsRefetch,
}: Props) {
  const { mutateAsync: deleteCandidateBoard } = useDeleteCandidateBoard();
  const { visibleFields } = useBoardCardVisibleFields();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isColorMenuOpen, setIsColorMenuOpen] = useState(false);
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const [titleValue, setTitleValue] = useState(title);
  const textFieldRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLDivElement>(null);
  const colorMenuRef = useRef<HTMLDivElement>(null);
  const sortMenuRef = useRef<HTMLDivElement>(null);
  const profilesContainerRef = useRef<HTMLDivElement>(null);
  const { submenuSideClass, resolveSide } = useSubmenuSide();

  const handleScroll = useCallback(
    throttle(() => {
      if (!profilesContainerRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = profilesContainerRef.current;
      const distanceToBottom = scrollHeight - (scrollTop + clientHeight);

      if (distanceToBottom < 200 && hasNextPage && !isLoadingMore && onLoadMore) {
        if (id) {
          onLoadMore(id);
        } else if (divideStatus === 'uncategorized') {
          onLoadMore('uncategorized');
        }
      }
    }, 200),
    [hasNextPage, isLoadingMore, onLoadMore, id, divideStatus]
  );

  const handleScrollEnd = useCallback(() => {
    if (!profilesContainerRef.current || !onLoadMore || isLoadingMore) return;

    const { scrollHeight, clientHeight, scrollTop } = profilesContainerRef.current;
    const distanceToBottom = scrollHeight - (scrollTop + clientHeight);

    if (distanceToBottom < 200 && hasNextPage) {
      if (id) {
        onLoadMore(id);
      } else if (divideStatus === 'uncategorized') {
        onLoadMore('uncategorized');
      }
    }
  }, [hasNextPage, isLoadingMore, onLoadMore, id, divideStatus]);

  const scrollTimer = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const profilesContainer = profilesContainerRef.current;
    if (profilesContainer) {
      profilesContainer.addEventListener('scroll', handleScroll);

      const detectScrollEnd = () => {
        if (scrollTimer.current) {
          clearTimeout(scrollTimer.current);
        }

        scrollTimer.current = setTimeout(() => {
          handleScrollEnd();
        }, 200);
      };

      profilesContainer.addEventListener('scroll', detectScrollEnd);

      const timer = setTimeout(() => {
        handleScrollEnd();
      }, 500);

      return () => {
        profilesContainer.removeEventListener('scroll', handleScroll);
        profilesContainer.removeEventListener('scroll', detectScrollEnd);
        if (scrollTimer.current) {
          clearTimeout(scrollTimer.current);
        }
        clearTimeout(timer);
      };
    }
  }, [handleScroll, handleScrollEnd]);

  useEffect(() => {
    if (profiles.length > 0) {
      const timer = setTimeout(() => {
        handleScrollEnd();
      }, 500);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [profiles.length, handleScrollEnd]);

  useEffect(() => {
    if (divideStatus === 'uncategorized' && hasNextPage && !isLoadingMore && onLoadMore) {
      const checkAndLoadMore = () => {
        if (!profilesContainerRef.current) return;

        const { scrollHeight, clientHeight } = profilesContainerRef.current;
        if (scrollHeight <= clientHeight + 100 || profiles.length < 10) {
          onLoadMore('uncategorized');
        }
      };

      const timer = setTimeout(checkAndLoadMore, 500);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [divideStatus, hasNextPage, isLoadingMore, onLoadMore, profiles.length]);

  // NOTE: メニュー外のクリックを検知してメニューを閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // オートメーションメニューが開いている場合、メニュー外のクリックでメニューを閉じる
      if (
        isSortMenuOpen &&
        sortMenuRef.current &&
        !sortMenuRef.current.contains(event.target as Node)
      ) {
        setIsSortMenuOpen(false);
      }

      // カラーメニューが開いている場合、カラーメニュー外のクリックでカラーメニューを閉じる
      if (
        isColorMenuOpen &&
        colorMenuRef.current &&
        !colorMenuRef.current.contains(event.target as Node)
      ) {
        setIsColorMenuOpen(false);
      }

      // メニューが開いている場合、メニュー外のクリックでメニューを閉じる
      if (
        isMenuOpen &&
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen, isColorMenuOpen, isSortMenuOpen]);

  const boardClasses = clsx(
    'w-[272px] min-w-[272px] rounded-sm px-sm py-md shadow-layer1 transition-colors duration-200',
    colorType === 'gray' && 'bg-[#EAEAEA]',
    colorType === 'green' && 'bg-green-50',
    colorType === 'blue' && 'bg-blue-50',
    colorType === 'yellow_green' && 'bg-lime-50',
    colorType === 'yellow' && 'bg-yellow-50',
    colorType === 'orange' && 'bg-[#FFF7EC]',
    colorType === 'red' && 'bg-red-50',
    colorType === 'pink' && 'bg-magenta-50',
    colorType === 'purple' && 'bg-purple-50'
  );

  const handleMenuClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsColorMenuOpen(false);
    setIsSortMenuOpen(false);

    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <Droppable droppableId={divideStatus} isDropDisabled={divideStatus === 'uncategorized'}>
      {(provided) => (
        <div className="relative">
          <div ref={provided.innerRef} {...provided.droppableProps} className={clsx(boardClasses)}>
            <div className="mb-md flex h-xl items-center">
              {isEditingTitle ? (
                <div className="w-[206px]">
                  <TextField
                    ref={textFieldRef}
                    value={titleValue}
                    onChange={(e) => setTitleValue(e.target.value)}
                    onBlur={() => {
                      setIsEditingTitle(false);

                      if (onTitleChange && titleValue !== title) {
                        onTitleChange(titleValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        textFieldRef.current?.blur();
                      }
                    }}
                    height={32}
                  />
                </div>
              ) : (
                <p className="line-clamp-1 max-w-[216px] text-heading-sm">{title}</p>
              )}

              {totalCount !== 0 && !isEditingTitle && (
                <span className="ml-sm flex h-lg min-w-lg items-center justify-center rounded-full bg-primary px-xs text-heading-3xs text-white">
                  {totalCount}
                </span>
              )}

              {/* メニューボタン */}
              {divideStatus !== 'uncategorized' &&
                divideStatus !== 'finished' &&
                boards?.records?.find((b) => `board_${b.id}` === divideStatus)?.divide_status !==
                  'finished' && (
                  <div className="relative ml-auto">
                    <div
                      ref={menuButtonRef}
                      role="button"
                      tabIndex={0}
                      className={clsx(
                        'flex size-icon-md cursor-pointer items-center justify-center rounded-sm hover:bg-gray_darken',
                        isMenuOpen && 'bg-gray_darken'
                      )}
                      onClick={handleMenuClick}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleMenuClick(e);
                        }
                      }}
                    >
                      <img
                        src="/agent/candidates/icon-more.svg"
                        width={24}
                        height={24}
                        alt="メニュー"
                      />
                    </div>

                    {/* メニュー一覧 */}
                    {isMenuOpen && (
                      <div
                        ref={menuRef}
                        className="absolute top-full right-0 z-[101] mt-xxs flex w-[150px] cursor-pointer flex-col rounded-sm bg-white shadow-layer3"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        role="menu"
                      >
                        {BOARD_MENU_ITEMS.map((item, key) => (
                          <div key={key} className="relative">
                            <div
                              className="flex h-xxl items-center px-sm hover:bg-gray_darken"
                              role="menuitem"
                              onMouseDown={(e) => e.stopPropagation()}
                              onPointerDown={(e) => e.stopPropagation()}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (item.option === 'nameEdit') {
                                  setIsEditingTitle(true);
                                  setIsMenuOpen(false);
                                  setIsColorMenuOpen(false);
                                  setIsSortMenuOpen(false);
                                  setTimeout(() => {
                                    textFieldRef.current?.focus();
                                  }, 0);
                                } else if (item.option === 'autoSort') {
                                  resolveSide(menuRef.current);
                                  setIsSortMenuOpen(true);
                                  setIsColorMenuOpen(false);
                                } else if (item.option === 'colorEdit') {
                                  resolveSide(menuRef.current);
                                  setIsColorMenuOpen(true);
                                  setIsSortMenuOpen(false);
                                } else if (item.option === 'delete' && onDelete && id) {
                                  const handleDelete = async () => {
                                    try {
                                      setIsMenuOpen(false);
                                      setIsColorMenuOpen(false);
                                      setIsSortMenuOpen(false);

                                      onDelete(id);

                                      await deleteCandidateBoard({ targetBoardId: id });
                                    } catch (error) {
                                      showErrorToast('ボードの削除に失敗しました');
                                    }
                                  };
                                  handleDelete();
                                }
                              }}
                            >
                              <p
                                className={clsx(
                                  'text-body-xs',
                                  item.option === 'delete' && 'text-danger'
                                )}
                              >
                                {item.label}
                              </p>
                              {(item.option === 'autoSort' || item.option === 'colorEdit') && (
                                <img
                                  src="/agent/candidates/icon-keyboard-arrow-right.svg"
                                  width={16}
                                  height={16}
                                  alt=""
                                  className="ml-auto"
                                />
                              )}
                            </div>

                            {item.option === 'autoSort' && isSortMenuOpen && (
                              <div
                                ref={sortMenuRef}
                                className={clsx('absolute top-0 z-[102]', submenuSideClass)}
                              >
                                <CandidatesSelectSortMenu
                                  divideStatus={
                                    boards?.records?.find((b) => `board_${b.id}` === divideStatus)
                                      ?.divide_status || ''
                                  }
                                  boardId={id}
                                  title={title}
                                  orderNumber={0}
                                  colorType={colorType}
                                  onClose={() => setIsSortMenuOpen(false)}
                                  onUpdateSuccess={() => {
                                    setIsMenuOpen(false);
                                    if (boardsRefetch) {
                                      boardsRefetch({ resetPages: true });
                                    }
                                  }}
                                />
                              </div>
                            )}

                            {item.option === 'colorEdit' && isColorMenuOpen && (
                              <div
                                ref={colorMenuRef}
                                className={clsx('absolute top-0 z-[102]', submenuSideClass)}
                              >
                                <CandidatesSelectColorMenu
                                  colorType={colorType}
                                  onColorSelect={(color) => {
                                    setIsColorMenuOpen(false);
                                    setIsMenuOpen(false);

                                    if (onColorChange && id) {
                                      onColorChange(color);
                                    }
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
            </div>

            <div
              ref={profilesContainerRef}
              className="hidden-scrollbar flex max-h-[calc(100vh_-_237px)] flex-col gap-xs overflow-hidden overflow-y-auto p-xxs"
              data-testid="board-profiles-container"
              onScroll={handleScroll}
            >
              {profiles.length === 0 ? (
                !isLoadingMore && (
                  <div className="flex min-h-[104px] items-center justify-center rounded-sm bg-white drop-shadow-[0px_0px_3px_var(--color-shadow)]">
                    <p className="text-heading-xxs text-main opacity-50">データがありません</p>
                  </div>
                )
              ) : (
                <>
                  {profiles.map((profile, index) => (
                    <DraggableProfileCard
                      key={profile.id}
                      profile={profile}
                      onProfileClick={onProfileClick}
                      index={index}
                      visibleFields={visibleFields}
                    />
                  ))}
                </>
              )}
              {provided.placeholder}

              {isLoadingMore && (
                <div className="flex h-[50px] items-center justify-center">
                  <div className="size-icon-xs animate-spin rounded-full border-2 border-solid border-primary border-t-transparent"></div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Droppable>
  );
}
